import { type ColumnType, formatDate } from "@similarweb/pro-logic-components";

import {
    ChangePercentage,
    CountryCell,
    DefaultCell,
    GroupTrafficShare,
    TrafficShareWithVisits,
} from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import type { IDurationObject } from "app/services/DurationService";

import type { TrafficByCountryVariant, TableData } from "./types";
import { volumeFormatter } from "./utils";

type Column = ColumnType<TableData>;

const TRAFFIC_SHARE_TOOLTIP_KEY = "demand.analysis.search.trends.map.tooltip.split.title";

const countryColumn: Column = {
    dataIndex: "country",
    get title() {
        return translate("global.country");
    },
    render: (value) => {
        return <CountryCell value={value} />;
    },
    defaultSortOrder: "descend",
};

const volumeShare: Column = {
    dataIndex: "volumeShare",
    get title() {
        return translate("demand.analysis.search.trends.map.table.share.single");
    },
    render: (value, row) => {
        return (
            <TrafficShareWithVisits
                row={row}
                value={value}
                totalVisitsRowProp="currentVolume"
                visitMinThreshold={0}
            />
        );
    },
};

const changeColumn = (isCompare: boolean, isYearOverYear?: boolean): Column => ({
    dataIndex: "change",
    title: translate("common.tooltip.change.pop", {
        comparedPeriod: !isCompare || isYearOverYear ? translate("common.yoy") : translate("common.pop"),
    }),
    align: "right",
    render: (value, row) => {
        return (
            <ChangePercentage
                row={row}
                value={value}
            />
        );
    },
});

const currentTrafficVolumeColumn = ({ from, to }: IDurationObject<string>): Column => ({
    dataIndex: "currentVolume",
    title: formatDate(from, to),
    align: "right",
    render: (value) => {
        return <DefaultCell value={volumeFormatter(value)} />;
    },
});

const previousTrafficVolumeColumn = ({ compareFrom, compareTo }: IDurationObject<string>): Column => ({
    dataIndex: "previousVolume",
    title: formatDate(compareFrom, compareTo),
    align: "right",
    render: (value) => {
        return <DefaultCell value={volumeFormatter(value)} />;
    },
});

const groupTrafficShareColumn: Column = {
    dataIndex: "trafficDistribution",
    get title() {
        return translate("demand.analysis.search.trends.map.table.share.group");
    },
    render: (value) => {
        return (
            <GroupTrafficShare
                value={value}
                tooltipKey={TRAFFIC_SHARE_TOOLTIP_KEY}
            />
        );
    },
};

const volumeColumn: Column = {
    dataIndex: "currentVolume",
    get title() {
        return translate("common.volume");
    },
    render: (value) => {
        return <DefaultCell value={volumeFormatter(value)} />;
    },
    align: "right",
    width: 80,
};

const previousTrafficSharePercentageColumn = (duration: IDurationObject<string>): Column => ({
    dataIndex: "previousPeriodTrafficDistribution",
    title: formatDate(duration.compareFrom, duration.compareTo),
    render: (value) => {
        return (
            <GroupTrafficShare
                value={value}
                tooltipKey={TRAFFIC_SHARE_TOOLTIP_KEY}
            />
        );
    },
});

const currentTrafficSharePercentageColumn = (duration: IDurationObject<string>): Column => ({
    dataIndex: "trafficDistribution",
    title: formatDate(duration.from, duration.to),
    render: (value) => {
        return (
            <GroupTrafficShare
                value={value}
                tooltipKey={TRAFFIC_SHARE_TOOLTIP_KEY}
            />
        );
    },
});

const emptyColumn: Column = {
    title: "",
    width: 12,
};

export const getTrafficByCountryTableColumns = (
    variant: TrafficByCountryVariant,
    duration: IDurationObject<string>,
    isYearOverYear: boolean,
): Column[] => {
    switch (true) {
        case variant === "SinglePeriod":
            return [countryColumn, volumeShare, changeColumn(false), emptyColumn];
        case variant === "Comparison":
            return [countryColumn, groupTrafficShareColumn, volumeColumn, emptyColumn];
        case variant === "PeriodOverPeriod":
            return [
                countryColumn,
                previousTrafficVolumeColumn(duration),
                currentTrafficVolumeColumn(duration),
                changeColumn(true, isYearOverYear),
                emptyColumn,
            ];
        case variant === "ComparisonAndPoP":
            return [
                emptyColumn,
                countryColumn,
                previousTrafficSharePercentageColumn(duration),
                currentTrafficSharePercentageColumn(duration),
                emptyColumn,
            ];
        default:
            return [];
    }
};
